// Client for the Abrar Platform API (/api/v1/*, v0.6.0), fronted by Authentik's
// embedded outpost via Caddy forward_auth (ARCHITECTURE.md §7a).
//
// Why this wrapper exists, not a plain fetch(): a valid, shared Authentik
// session cookie is NOT enough on its own to pass the outpost's forward-auth
// check. The first request behind the maps-api Proxy Provider still needs one
// top-level, full-page visit to /outpost.goauthentik.io/start to mint the
// outpost's own provider-specific cookie — confirmed live, 2026-09-28
// (docs/deployment.md v0.6.0 step 3). A fetch() alone can never complete that
// handshake: the browser won't follow the redirect chain from an XHR the way
// it follows a full-page navigation. Once that one hop has happened, ordinary
// fetch() calls succeed indefinitely, so this only ever fires once per tab.
//
// sessionStorage (not a cookie) guards against a redirect loop if the caller
// is genuinely unauthenticated for some other reason (expired Authentik
// session, no session at all) — in that case the outpost's own login page is
// shown once, same as any other Authentik-protected app, rather than
// bouncing forever between the SPA and the outpost.

const START_URL = '/outpost.goauthentik.io/start';
const HANDSHAKE_DONE_KEY = 'abrar-platform-api-outpost-handshake';

/** True once this tab has already completed (or attempted) the outpost's
 * one-time authorization hop this session. */
function handshakeAttempted(): boolean {
  try {
    return sessionStorage.getItem(HANDSHAKE_DONE_KEY) === '1';
  } catch {
    return false; // private browsing / storage blocked — fall back to no guard
  }
}

function markHandshakeAttempted(): void {
  try {
    sessionStorage.setItem(HANDSHAKE_DONE_KEY, '1');
  } catch {
    // ignore — worst case we redirect through /start again next time
  }
}

/** Sends the browser through the outpost's one-time authorization hop, then
 * back to `returnTo` (default: the current page). This is a full top-level
 * navigation, not a fetch — the call never returns. */
export function startOutpostHandshake(returnTo: string = window.location.pathname + window.location.search): void {
  markHandshakeAttempted();
  window.location.href = `${START_URL}?rd=${encodeURIComponent(returnTo)}`;
}

/**
 * fetch() against a Platform API endpoint (e.g. '/api/v1/me'). On a 401 that
 * looks like "the outpost never saw a session" (not yet attempted this tab),
 * performs the one-time /start handshake instead of returning the 401 —
 * control does not return to the caller in that case, since the page
 * navigates away and back.
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(path, { ...init, credentials: 'include' });

  if (res.status === 401 && !handshakeAttempted()) {
    startOutpostHandshake();
    return new Promise<Response>(() => {}); // navigating away; never resolves
  }

  if (res.status !== 401) {
    // A real answer came back — clear the guard so a later genuine session
    // expiry gets one more handshake attempt instead of being stuck.
    try {
      sessionStorage.removeItem(HANDSHAKE_DONE_KEY);
    } catch {
      // ignore
    }
  }

  return res;
}
